import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import * as S from "./styles";

import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { IChatMessages, ISureBets } from "@/interfaces/BetChatTypes";
import SampleLiveGames from "@/components/SampleLiveGames";
import AdAppCoupon from "@/components/AdAppCoupon";
import gptImg from "@/assets/dashboard-home/chat/gpt-img.png";
import { Controller, useForm } from "react-hook-form";
import { api } from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { FaRegUser, FaSearch } from "react-icons/fa";
import { IoSend } from "react-icons/io5";
import { toast } from "react-toastify";
import RoundInput from "@/components/RoundInput";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { FutButton } from "@/components/FutButton";

export default function Chat() {
  const theme = useTheme();
  const IsMedia1200 = !useMediaQuery(theme.breakpoints.up("lg"));
  const { decodedToken } = useAuth();

  const [chatScreens, setChatScreen] = useState<ISureBets[] | IChatMessages[]>(
    []
  );
  const [sureBets, setSureBets] = useState<ISureBets[]>([]);
  const [chatGpt, setChatGpt] = useState<IChatMessages[]>([]);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [searchModal, setSearchModal] = useState(false);

  const chatRef = useRef<HTMLDivElement>(null);

  const chatForm = useForm({
    defaultValues: {
      isGptChat: false,
      userText: "",
      searchMatch: "",
    },
  });

  const watchGptChat = chatForm.watch("isGptChat");
  const watchSearchMatch = chatForm.watch("searchMatch");

  useEffect(() => {
    if (watchGptChat) {
      setChatScreen(chatGpt);
      setLoading(false);
      return;
    }

    if (!sureBets.length) {
      setLoading(true);
    }

    setChatScreen(sureBets);
  }, [watchGptChat]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [chatGpt]);

  useEffect(() => {
    const wsUrl = "wss://fut-gpt-back-staging-b601d473e9de.herokuapp.com";
    const newWs = new WebSocket(wsUrl);

    newWs.onopen = () => {
      console.log("Conectado ao servidor WebSocket.");
    };

    newWs.onmessage = (event) => {
      console.log(event)
      const newMessage = JSON.parse(event.data).data;
      if (!newMessage.includes("-")) {
        setChatScreen(newMessage);
        setSureBets(newMessage);
        setLoading(false);
      }
    };
    newWs.onclose = () => {
      console.log("Conexão WebSocket fechada.");
    };

    return () => {
      if (newWs && newWs.readyState === WebSocket.OPEN) {
        newWs.close();
      }
    };
  }, []);

  const findSureBets = () => {
    const query = chatForm.getValues("searchMatch");

    chatForm.setValue("searchMatch", "");

    if (!query) {
      setChatScreen(sureBets);
      setSearchModal(false);
      return;
    }

    const filteredBets = chatScreens.filter((obj) =>
      obj.text.toLocaleLowerCase().includes(query.toLocaleLowerCase())
    );

    setChatScreen(filteredBets as ISureBets[]);
    setSearchModal(false);
  };

  const submitQuestion = async () => {
    try {
      const userInput = chatForm.getValues("userText");
      if (!userInput) {
        return;
      }
      chatForm.setValue("userText", "");

      setMsgLoading(true);
      setChatScreen(
        (prev) =>
          [...prev, { text: userInput, isUser: true }] as IChatMessages[]
      );
      setChatGpt((prev) => [...prev, { text: userInput, isUser: true }]);

      const res = await api.post(`/chat/get-completions/${decodedToken.id}`, {
        prompt: userInput,
      });

      setMsgLoading(false);
      setChatScreen(
        (prev) =>
          [...prev, { text: res.data.body, isUser: false }] as IChatMessages[]
      );
      setChatGpt((prev) => [...prev, { text: res.data.body, isUser: false }]);
    } catch (e) {
      toast.error("Ocorreu um erro ao tentar enviar a mensagem!");
      setMsgLoading(false);
    }
  };

  return (
    <>
      <S.MainWrapper>
        <Grid container>
          {IsMedia1200 ? null : (
            <Grid item sm={2}>
              <SampleLiveGames />
            </Grid>
          )}
          <Grid
            item
            sm={IsMedia1200 ? 12 : 8}
            width={IsMedia1200 ? "100%" : undefined}
            height="85vh"
          >
            <S.GptWrapper>
              <S.GptContainer ref={chatRef}>
                <S.GptHeader>
                  <h2>Análise GPT</h2>
                  <div>
                    <button
                      disabled={watchGptChat}
                      onClick={() => setSearchModal((prev) => !prev)}
                    >
                      <FaSearch />
                    </button>
                    {decodedToken.isStaff ? (
                      <>
                        <p>SureBets</p>
                        <Controller
                          name="isGptChat"
                          control={chatForm.control}
                          render={({ field: { onChange, value } }) => (
                            <Switch checked={value} onChange={onChange} />
                          )}
                        />
                        <p>ChatGpt</p>
                      </>
                    ) : null}
                  </div>
                </S.GptHeader>
                {!loading ? (
                  watchGptChat ? (
                    <>
                      <S.WideBox></S.WideBox>
                      <S.MessageWrapper>
                        {chatGpt.map((el) => (
                          <S.MessagesBox>
                            <S.ImgContMsg>
                              {el.isUser ? <FaRegUser /> : <img src={gptImg} />}
                            </S.ImgContMsg>
                            <p>{el.text}</p>
                          </S.MessagesBox>
                        ))}
                        {msgLoading ? (
                          <S.MessagesBox>
                            <CircularProgress />
                          </S.MessagesBox>
                        ) : (
                          <></>
                        )}
                      </S.MessageWrapper>
                    </>
                  ) : (
                    <S.MessageWrapper>
                      {chatScreens.map((obj, index) => (
                        <S.MessagesBox key={index}>
                          <p
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(obj.text, {
                                ADD_ATTR: ["target"],
                              }),
                            }}
                          />
                        </S.MessagesBox>
                      ))}
                    </S.MessageWrapper>
                  )
                ) : (
                  <S.GptTryingFound>
                    <CircularProgress />
                    <p>Procurando os melhores jogos pra você</p>
                  </S.GptTryingFound>
                )}
              </S.GptContainer>
              <S.InputChatContainer>
                <S.GptChatBox
                  {...chatForm.register("userText")}
                  placeholder="Pergunte Algo Para o Nosso Bot..."
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      submitQuestion();
                    }
                  }}
                  disabled={!watchGptChat}
                />
                <div>
                  <button onClick={submitQuestion} disabled={!watchGptChat}>
                    <IoSend />
                  </button>
                </div>
              </S.InputChatContainer>
            </S.GptWrapper>
          </Grid>
        </Grid>
      </S.MainWrapper>
      <Modal open={searchModal} onClose={() => setSearchModal(false)}>
        <S.ModalContainer>
          <S.ModalContent>
            <h2>
              Pesquisar
              <span onClick={() => setSearchModal(false)}>X</span>
            </h2>

            <p>Pesquisar em Surebets:</p>
            <RoundInput
              inputName="searchMatch"
              control={chatForm.control}
              endAdornment={
                <InputAdornment position="end">
                  {watchSearchMatch ? (
                    <IconButton
                      onClick={() => {
                        chatForm.setValue("searchMatch", "");
                      }}
                      edge="end"
                    >
                      <IoIosCloseCircleOutline />
                    </IconButton>
                  ) : (
                    <FaSearch />
                  )}
                </InputAdornment>
              }
              fullWidth
              size="small"
              autoComplete="email"
            />

            <FutButton sx={{ px: 3, mt: 2 }} onClick={findSureBets}>
              Aplicar Filtro
            </FutButton>
          </S.ModalContent>
        </S.ModalContainer>
      </Modal>
    </>
  );
}
